import { Event, EventType, EventSubType, EventTaxonomyInUsePlace, EventTypeCatalogUsedEvent, EventTypeDetail, EventSubTypeDetail } from "../models/events";
import { isFeaturedActiveInCarousel } from "./eventSchedule";
import { User, TenantListItem, Tenant } from "../models/users";
import { UserNotification } from "../models/notifications";
import { Venue } from "../models/venues";
import {
  SupportCategory,
  SupportArticleListItem,
  SupportArticleDetail,
  SupportArticleVersion,
  SupportContactReason,
  SupportConfig,
  SupportContactItem,
  SupportContactsResult,
  SupportFeedbackDashboardItem,
} from "../models/support";
import { toIsoInstantString } from "./functions/parseInstant";

/**
 * Convierte un valor a número de forma segura.
 * Retorna 0 si el valor es inválido.
 */
const toNumber = (value: any): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

/**
 * Convierte un valor a booleano de forma segura.
 * Maneja 1/0, "true"/"false", etc.
 */
const toBoolean = (value: any): boolean => {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  return false;
};

/**
 * Asegura que una fecha sea un string ISO válido o null.
 */
const toIsoDateString = (value: any): string | undefined => {
  if (!value) return undefined;
  try {
    const date = new Date(value);
    return isNaN(date.getTime()) ? undefined : date.toISOString();
  } catch (e) {
    return undefined;
  }
};

/**
 * Mapper robusto para Eventos.
 * Transforma la respuesta cruda del backend al tipo Event estricto.
 */
export const mapEvent = (data: any): Event => {
  if (!data) return {} as Event;

  const rawVenueId = data.venueId ?? data.venue_id;
  const rawRecurrenceGroupId = data.recurrenceGroupId ?? data.recurrence_group_id;
  const rawRecurrenceSignupWeeks = data.recurrenceSignupWeeks ?? data.recurrence_signup_weeks;
  const rawBonusPoints = data.bonusPoints ?? data.bonus_points;

  return {
    ...data,
    // Conversiones numéricas críticas
    id: toNumber(data.id),
    guests: toNumber(data.guests),
    amount: toNumber(data.amount),
    price: data.price !== null && data.price !== undefined ? toNumber(data.price) : null,
    inscription: toNumber(data.inscription),
    currentParticipants: toNumber(data.currentParticipants),
    available: toNumber(data.available),
    users: toNumber(data.users),
    fictional: toNumber(data.fictional),
    eventTypeId: toNumber(data.eventTypeId),
    eventSubTypeId: toNumber(data.eventSubTypeId),
    
    // paymentType inferido desde inscription
    paymentType: toNumber(data.inscription) === 0 ? "FREE" : "GRUPS",

    // Conversiones booleanas
    active: toBoolean(data.active),
    hostAssists: toBoolean(data.hostAssists),
    isRecurring: toBoolean(data.isRecurring),
    suppressNotifications: toBoolean(data.suppressNotifications ?? data.suppress_notifications),
    suppressRatingPrompt: toBoolean(data.suppressRatingPrompt ?? data.suppress_rating_prompt),
    hasCheckins: toBoolean(data.hasCheckins ?? data.has_checkins),
    featured: (() => {
      if (data.featured !== undefined && data.featured !== null) {
        return toBoolean(data.featured);
      }
      const order =
        data.featuredOrder !== null && data.featuredOrder !== undefined
          ? toNumber(data.featuredOrder)
          : null;
      if (!data.startDate) return order !== null;
      return isFeaturedActiveInCarousel({
        featuredOrder: order,
        startDate: data.startDate,
        registrationDeadlineMinutes:
          data.registrationDeadline !== null && data.registrationDeadline !== undefined
            ? toNumber(data.registrationDeadline)
            : null,
        duration: data.duration ?? null,
      });
    })(),
    
    // Normalización de fechas
    startDate: toIsoDateString(data.startDate) || data.startDate, // Fallback al original si falla parseo
    endDate: toIsoDateString(data.endDate),
    registrationDeadline:
      data.registrationDeadline !== null && data.registrationDeadline !== undefined
        ? toNumber(data.registrationDeadline)
        : null,
    featuredOrder: data.featuredOrder !== null && data.featuredOrder !== undefined ? toNumber(data.featuredOrder) : null,
    venueId: rawVenueId !== null && rawVenueId !== undefined ? toNumber(rawVenueId) : null,
    recurrenceGroupId: rawRecurrenceGroupId ?? null,
    recurrenceSignupWeeks:
      rawRecurrenceSignupWeeks !== null && rawRecurrenceSignupWeeks !== undefined
        ? toNumber(rawRecurrenceSignupWeeks)
        : null,
    bonusPoints:
      rawBonusPoints !== null && rawBonusPoints !== undefined ? toNumber(rawBonusPoints) : null,
    createdAt: toIsoDateString(data.createdAt),
    updatedAt: toIsoDateString(data.updatedAt),
    
    // Manejo de objetos anidados (si existen)
    eventType: data.eventType ? {
      ...data.eventType,
      id: toNumber(data.eventType?.id),
      subType: data.eventType?.subType ? {
        ...data.eventType.subType,
        id: toNumber(data.eventType.subType?.id)
      } : undefined
    } : undefined,
    
    host: data.host ? {
      ...data.host,
      id: toNumber(data.host?.id)
    } : undefined,

    rating: data.rating ? {
      average: toNumber(data.rating?.average),
      count: toNumber(data.rating?.count)
    } : undefined
  };
};

/**
 * Mapper para Subtipos de Evento
 */
const mapEventTaxonomyInUsePlaces = (data: unknown): EventTaxonomyInUsePlace[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.flatMap((place) => {
    if (!place || typeof place !== "object") {
      return [];
    }
    const record = place as { field?: unknown; message?: unknown };
    const field = typeof record.field === "string" ? record.field.trim() : "";
    const message = typeof record.message === "string" ? record.message.trim() : "";
    if (!field || !message) {
      return [];
    }
    return [{ field, message }];
  });
};

export const mapEventSubType = (data: any): EventSubType => {
  if (!data) return {} as EventSubType;
  return {
    eventSubTypeId: toNumber(data.eventSubTypeId || data.id),
    eventSubTypeName: data.eventSubTypeName || data.name,
    eventTypeId: toNumber(data.eventTypeId),
    eventTypeName: data.eventTypeName,
    createDate: toIsoDateString(data.createDate) || data.createDate,
    tenantId: data.tenantId ?? data.tenant_id ?? null,
    inUse: data.inUse === true,
    inUsePlaces: mapEventTaxonomyInUsePlaces(data.inUsePlaces),
  };
};

/**
 * Mapper para Tipos de Evento
 */
export const mapEventType = (data: any): EventType => {
  if (!data) return {} as EventType;
  return {
    ...data,
    eventTypeId: toNumber(data.eventTypeId),
    tenantId: data.tenantId ?? data.tenant_id ?? null,
    inUse: data.inUse === true,
    inUsePlaces: mapEventTaxonomyInUsePlaces(data.inUsePlaces),
    subTypes: Array.isArray(data.subTypes) ? data.subTypes.map(mapEventSubType) : []
  };
};

export const mapEventTypeCatalogUsedEvent = (data: any): EventTypeCatalogUsedEvent => ({
  id: toNumber(data?.id),
  name: typeof data?.name === "string" ? data.name : "",
  startDate: toIsoDateString(data?.startDate) || data?.startDate || "",
  tenantId: data?.tenantId ?? data?.tenant_id ?? null,
});

export const mapEventTypeDetail = (data: any): EventTypeDetail => ({
  ...mapEventType(data),
  usedInEvents: Array.isArray(data?.usedInEvents)
    ? data.usedInEvents.map(mapEventTypeCatalogUsedEvent)
    : [],
});

export const mapEventSubTypeDetail = (data: any): EventSubTypeDetail => {
  const updatedAtRaw = data?.updatedAt;
  const updatedAt =
    typeof updatedAtRaw === "string" && updatedAtRaw.trim()
      ? toIsoDateString(updatedAtRaw) || updatedAtRaw
      : null;
  return {
    ...mapEventSubType(data),
    updatedAt,
    usedInEvents: Array.isArray(data?.usedInEvents)
      ? data.usedInEvents.map(mapEventTypeCatalogUsedEvent)
      : [],
  };
};

/**
 * Mapper para Notificaciones
 */
export const mapNotification = (data: any): UserNotification => {
  if (!data) return {} as UserNotification;
  return {
    ...data,
    id: toNumber(data.id),
    read: toBoolean(data.read),
    createdAt: toIsoDateString(data.createdAt) || data.createdAt,
    eventId: data.eventId ? toNumber(data.eventId) : undefined,
    senderId: data.senderId ? toNumber(data.senderId) : undefined
  };
};

export const mapTenant = (data: any): TenantListItem => {
  if (!data) return {} as TenantListItem;
  return {
    ...data,
    active: toBoolean(data.active),
    userCount: toNumber(data.userCount),
    createdAt: toIsoDateString(data.createdAt) || data.createdAt || new Date().toISOString()
  };
};

/**
 * Mapper para Tenant de usuario (con campo active)
 */
export const mapUserTenant = (data: any): Tenant => {
  if (!data) return {} as Tenant;
  return {
    ...data,
    active: data.active !== undefined ? toBoolean(data.active) : true,
    created_at: data.created_at || data.createdAt || new Date().toISOString(),
    createdAt: data.createdAt || data.created_at || new Date().toISOString()
  };
};

export const mapUser = (data: any): User => {
  if (!data) return {} as User;

  return {
    ...data,
    id: toNumber(data.id),
    userTypeId: toNumber(data.userTypeId),
    
    // Normalización de teléfono (string para consistencia)
    userPhone: data.userPhone?.toString() || "",
    
    // Fechas
    birthDate: toIsoDateString(data.birthDate) || data.birthDate,
    createdAt: toIsoDateString(data.createdAt),
    updatedAt: toIsoDateString(data.updatedAt),
    createDate: toIsoDateString(data.createDate),
    
    // Booleanos
    banned: toBoolean(data.banned),
    banStatus: data.banStatus || data.ban_status,
    banReason: data.banReason || data.ban_reason,
    banDate: toIsoDateString(data.banDate || data.ban_date) || data.banDate || data.ban_date,
    bannedBy: data.bannedBy || data.banned_by,
    
    // Tenants del usuario con campo active
    tenants: Array.isArray(data.tenants) ? data.tenants.map(mapUserTenant) : []
  };
};

/**
 * Mapper para Venues.
 * Transforma la respuesta cruda del backend al tipo Venue estricto.
 */
export const mapVenue = (data: any): Venue => {
  if (!data) return {} as Venue;

  return {
    ...data,
    id: toNumber(data.id),
    name: data.name || "",
    address: data.address || "",
    latitude: toNumber(data.latitude),
    longitude: toNumber(data.longitude),
    tenantIds: Array.isArray(data.tenantIds) ? data.tenantIds : [],
    eventCategories: Array.isArray(data.eventCategories) ? data.eventCategories : [],
    maxCapacity: data.maxCapacity !== null && data.maxCapacity !== undefined ? toNumber(data.maxCapacity) : null,
    amenities: Array.isArray(data.amenities) ? data.amenities : [],
    contacts: Array.isArray(data.contacts) ? data.contacts : [],
    status: data.status || "ACTIVE",
    rating: data.rating ? {
      average: toNumber(data.rating.average),
      count: toNumber(data.rating.count),
    } : undefined,
    comments: Array.isArray(data.comments) ? data.comments : undefined,
    createdAt: toIsoDateString(data.createdAt),
    updatedAt: toIsoDateString(data.updatedAt),
  };
};

/**
 * Mappers del módulo de Soporte y Ayuda (HELP-08).
 * El backend responde snake_case envuelto en { success, data }.
 */
export const mapSupportCategory = (data: any): SupportCategory => ({
  id: data.id,
  tenantId: data.tenant_id,
  tenantProvider: data.tenant_provider ?? null,
  name: data.name || "",
  icon: data.icon ?? null,
  description: data.description ?? null,
  sortOrder: toNumber(data.sort_order),
  isActive: toBoolean(data.is_active),
  articleCount: toNumber(data.article_count),
  createdAt: toIsoInstantString(data.created_at) ?? data.created_at ?? "",
  updatedAt: toIsoInstantString(data.updated_at) ?? data.updated_at ?? "",
});

export const mapSupportArticle = (data: any): SupportArticleListItem => ({
  id: data.id,
  tenantId: data.tenant_id,
  categoryId: data.category_id,
  categoryName: data.category_name ?? null,
  title: data.title || "",
  body: data.body || "",
  searchTags: Array.isArray(data.search_tags) ? data.search_tags : [],
  isFeatured: toBoolean(data.is_featured),
  featuredOrder: data.featured_order !== null && data.featured_order !== undefined ? toNumber(data.featured_order) : null,
  sortOrder: toNumber(data.sort_order),
  isActive: toBoolean(data.is_active),
  version: toNumber(data.version),
  feedbackYes: toNumber(data.feedback_yes),
  feedbackNo: toNumber(data.feedback_no),
  feedbackPct: data.feedback_pct !== null && data.feedback_pct !== undefined ? toNumber(data.feedback_pct) : null,
  createdAt: toIsoInstantString(data.created_at) ?? data.created_at ?? "",
  updatedAt: toIsoInstantString(data.updated_at) ?? data.updated_at ?? "",
});

export const mapSupportArticleVersion = (data: any): SupportArticleVersion => ({
  id: data.id,
  articleId: data.article_id,
  version: toNumber(data.version),
  title: data.title || "",
  body: data.body || "",
  changedBy: data.changed_by ?? null,
  changedByName: data.changed_by_name ?? null,
  changeSummary: data.change_summary ?? null,
  createdAt: toIsoInstantString(data.created_at) ?? data.created_at ?? "",
});

export const mapSupportArticleDetail = (data: any): SupportArticleDetail => ({
  ...mapSupportArticle(data),
  versions: Array.isArray(data.versions) ? data.versions.map(mapSupportArticleVersion) : [],
});

export const mapSupportContactReason = (data: any): SupportContactReason => ({
  id: data.id,
  tenantId: data.tenant_id,
  label: data.label || "",
  emailSubjectPrefix: data.email_subject_prefix || "",
  sortOrder: toNumber(data.sort_order),
  isActive: toBoolean(data.is_active),
  createdAt: toIsoInstantString(data.created_at) ?? data.created_at ?? "",
});

export const mapSupportConfig = (data: any): SupportConfig => ({
  tenantId: data.tenant_id,
  supportEmail: data.support_email ?? null,
  termsUrl: data.terms_url ?? null,
  maxFeaturedFaqs: toNumber(data.max_featured_faqs),
  confirmationMessage: data.confirmation_message ?? null,
  chatbotEnabled: toBoolean(data.chatbot_enabled),
  updatedAt: toIsoInstantString(data.updated_at) ?? data.updated_at ?? "",
});

export const mapSupportContactItem = (data: any): SupportContactItem => ({
  id: data.id,
  userId: toNumber(data.user_id),
  userName: data.user_name ?? null,
  userEmail: data.user_email ?? null,
  reasonId: data.reason_id ?? null,
  reasonLabel: data.reason_label ?? null,
  messagePreview: data.message_preview || "",
  message: data.message || "",
  attachmentUrl: data.attachment_url ?? null,
  emailSent: toBoolean(data.email_sent),
  readAt: data.read_at ? toIsoInstantString(data.read_at) ?? data.read_at : null,
  createdAt: toIsoInstantString(data.created_at) ?? data.created_at ?? "",
});

export const mapSupportContactsResult = (data: any): SupportContactsResult => ({
  items: Array.isArray(data.items) ? data.items.map(mapSupportContactItem) : [],
  total: toNumber(data.total),
  page: toNumber(data.page),
  pages: toNumber(data.pages),
});

export const mapSupportFeedbackDashboardItem = (data: any): SupportFeedbackDashboardItem => ({
  articleId: data.article_id,
  title: data.title || "",
  categoryName: data.category_name ?? null,
  feedbackYes: toNumber(data.feedback_yes),
  feedbackNo: toNumber(data.feedback_no),
  feedbackPct: data.feedback_pct !== null && data.feedback_pct !== undefined ? toNumber(data.feedback_pct) : null,
  needsReview: toBoolean(data.needs_review),
});
